import type React from 'react';
import type { ComponentProps, ReactNode } from 'react';
import {
  faCircleExclamation,
  faCircleInfo,
  faTriangleExclamation,
} from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { cva, type VariantProps } from 'class-variance-authority';

import { Icon } from '@/components/ui/icon';
import { cn } from '@/lib/utils';

const alertVariants = cva(
  'relative grid w-full grid-cols-[0_1fr] items-start gap-y-0.5 rounded-lg px-4 py-3 text-sm has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] has-[>svg]:gap-x-3 [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current',
  {
    variants: {
      variant: {
        info: 'text-primary bg-primary/4 [&>svg]:text-current',
        secondary: 'bg-secondary/40 text-secondary-foreground',
        warning: 'text-amber-600 bg-amber-500/4 [&>svg]:text-current',
        destructive:
          'text-destructive bg-destructive/4 *:data-[slot=alert-description]:text-destructive/90 [&>svg]:text-current',
      },
    },
    defaultVariants: {
      variant: 'info',
    },
  },
);

function Alert({
  className,
  variant,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  );
}

function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-title"
      className={cn('col-start-2 line-clamp-1 min-h-4 font-medium tracking-tight', className)}
      {...props}
    />
  );
}

function AlertDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        'col-start-2 grid justify-items-start gap-1 text-sm [&_p]:leading-relaxed',
        className,
      )}
      {...props}
    />
  );
}

type AlertVariant = NonNullable<VariantProps<typeof alertVariants>['variant']>;

interface StandardAlertProps extends Omit<ComponentProps<'div'>, 'children' | 'title'> {
  variant?: AlertVariant;
  title?: ReactNode;
  description?: ReactNode;
  icon?: IconDefinition;
  showIcon?: boolean;
}

const defaultIcons: Record<AlertVariant, IconDefinition> = {
  info: faCircleInfo,
  secondary: faCircleInfo,
  warning: faCircleExclamation,
  destructive: faTriangleExclamation,
};

function StandardAlert({
  variant = 'info',
  title,
  description,
  icon,
  showIcon = true,
  className,
  ...props
}: StandardAlertProps) {
  // If icon is provided, use it; otherwise use default icon if showIcon is true
  const iconToShow = icon ?? (showIcon ? defaultIcons[variant] : null);

  return (
    <Alert variant={variant} className={className} {...props}>
      {iconToShow && <Icon icon={iconToShow} />}
      {title && <AlertTitle>{title}</AlertTitle>}
      {description && <AlertDescription>{description}</AlertDescription>}
    </Alert>
  );
}

export { Alert, AlertDescription, AlertTitle, StandardAlert };
